import { Controller, FieldValues, Path } from 'react-hook-form';
import { Box, Button } from '@mui/material';
import { LdtOrderRepository } from '@ldt/api/order_repository';
import { FormSectionCard } from '@ldt/features/orders/components/form_section_card';
import { OrderingProviderAutocomplete } from '@ldt/features/orders/components/ordering_provider_autocomplete';
import { SectionHeader } from 'src/components/section_header';
import { FormFieldProps } from 'src/distribution_platforms/features/order_form';
import { OrderingProviderFieldsFragment } from 'src/types';

export const OrderingProviderFormSection = <T extends FieldValues>({
  control,
  errors,
  trigger,
  ldtOrderRepository,
  onEditProvider,
  onChangeProvider,
  onClickAddProvider,
  canShowAddProviderButton,
  selectedOrderingProvider,
}: FormFieldProps<T> & {
  ldtOrderRepository: LdtOrderRepository;
  onEditProvider: (provider: OrderingProviderFieldsFragment) => void;
  onChangeProvider: (provider: OrderingProviderFieldsFragment | null) => void;
  onClickAddProvider: () => void;
  canShowAddProviderButton: boolean;
  selectedOrderingProvider: OrderingProviderFieldsFragment | null;
}) => (
  <FormSectionCard
    header={<SectionHeader title='Ordering Provider' />}
    sx={{ borderBottom: 'none' }}
  >
    <Box data-testid='ordering-provider-form-section'>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 0.5, sm: 2 },
        }}
      >
        <Box sx={{ maxWidth: { sm: '400px' }, flex: 1 }}>
          <Controller
            name={'orderingProviderId' as Path<T>}
            control={control}
            render={({ field }) => (
              <OrderingProviderAutocomplete
                orderRepository={ldtOrderRepository}
                value={selectedOrderingProvider}
                onChange={async (provider) => {
                  field.onChange(provider?.id ?? '');
                  onChangeProvider(provider);
                  // Immediately trigger validation so any existing error
                  // message for ordering provider clears as soon as a
                  // valid provider is selected.
                  await trigger?.('orderingProviderId' as Path<T>);
                }}
                onBlur={field.onBlur}
                error={!!errors.orderingProviderId}
                helperText={
                  errors.orderingProviderId?.message?.toString() ?? ' '
                }
                onEdit={onEditProvider}
              />
            )}
          />
        </Box>
        {/* Hide ADD PROVIDER button for ordering physician users who already have a provider.
        While the ordering providers query is loading, we optimistically hide the button
        to avoid a flash of it appearing and then disappearing once data loads. */}
        {canShowAddProviderButton && (
          <Button
            variant='contained'
            size='large'
            sx={{ mb: '20px', width: { xs: '100%', sm: 'auto' } }}
            onClick={onClickAddProvider}
            data-testid='ordering-provider-add-button'
          >
            ADD PROVIDER
          </Button>
        )}
      </Box>
    </Box>
  </FormSectionCard>
);
